/* Global behaviors: drawers, search panel + predictive search, quantity steppers.
   Vanilla JS, no dependencies. Everything degrades: navigation, search and forms
   all work without JS (drawers stay hidden, search submits to /search). */
(function () {
  'use strict';

  var FOCUSABLE =
    'a[href], button:not([disabled]):not([tabindex="-1"]), input:not([type="hidden"]):not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* ---------- Drawers (mobile menu, filters) ---------- */
  var openDrawer = null;
  var lastTrigger = null;
  var savedScrollY = 0;

  function lockScroll() {
    savedScrollY = window.scrollY;
    document.body.style.position = 'fixed';
    document.body.style.top = -savedScrollY + 'px';
    document.body.style.width = '100%';
    document.body.style.overflow = 'hidden';
  }

  function unlockScroll() {
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.width = '';
    document.body.style.overflow = '';
    window.scrollTo(0, savedScrollY);
  }

  function visibleFocusable(container) {
    return Array.prototype.filter.call(
      container.querySelectorAll(FOCUSABLE),
      function (el) {
        return el.offsetWidth > 0 || el.offsetHeight > 0 || el === document.activeElement;
      }
    );
  }

  function setDrawerState(drawer, open, trigger) {
    if (open) {
      drawer.hidden = false;
      // Force a style flush so the transition runs after `hidden` is removed
      // (rAF stalls in occluded tabs and would leave the drawer half-open).
      void drawer.offsetWidth;
      drawer.setAttribute('data-open', '');
      openDrawer = drawer;
      lastTrigger = trigger || null;
      lockScroll();
      var focusable = visibleFocusable(drawer)[0];
      if (focusable) focusable.focus();
    } else {
      drawer.removeAttribute('data-open');
      // Let the slide-out transition finish before display:none kicks in.
      if (reducedMotion.matches) {
        drawer.hidden = true;
      } else {
        var onEnd = function () {
          drawer.hidden = true;
          drawer.removeEventListener('transitionend', onEnd);
        };
        drawer.addEventListener('transitionend', onEnd);
        window.setTimeout(onEnd, 300);
      }
      openDrawer = null;
      unlockScroll();
      if (lastTrigger) {
        lastTrigger.focus();
        lastTrigger.setAttribute('aria-expanded', 'false');
        lastTrigger = null;
      }
    }
  }

  document.addEventListener('click', function (event) {
    var opener = event.target.closest('[data-drawer-open]');
    if (opener) {
      var target = document.getElementById(opener.getAttribute('data-drawer-open'));
      if (target) {
        opener.setAttribute('aria-expanded', 'true');
        setDrawerState(target, true, opener);
      }
      return;
    }
    var closer = event.target.closest('[data-drawer-close]');
    if (closer && openDrawer) {
      setDrawerState(openDrawer, false);
    }
  });

  document.addEventListener('keydown', function (event) {
    if (event.key === 'Escape') {
      if (openDrawer) setDrawerState(openDrawer, false);
      var panel = document.querySelector('.search-panel[data-open]');
      if (panel) toggleSearch(panel, false);
      return;
    }
    // Focus trap: the open drawer is aria-modal, so Tab must cycle inside it.
    if (event.key === 'Tab' && openDrawer) {
      var focusable = visibleFocusable(openDrawer);
      if (!focusable.length) return;
      var first = focusable[0];
      var last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      } else if (!openDrawer.contains(document.activeElement)) {
        event.preventDefault();
        first.focus();
      }
    }
  });

  /* ---------- Header search panel + predictive search ---------- */
  function toggleSearch(panel, open) {
    var header = panel.closest('.site-header') || document;
    var toggle = header.querySelector('[data-search-toggle]');
    if (!toggle) return;
    if (open) {
      panel.setAttribute('data-open', '');
      toggle.setAttribute('aria-expanded', 'true');
      var input = panel.querySelector('[data-predictive-input]');
      if (input) input.focus();
    } else {
      panel.removeAttribute('data-open');
      toggle.setAttribute('aria-expanded', 'false');
      toggle.focus();
    }
  }

  document.addEventListener('click', function (event) {
    var toggle = event.target.closest('[data-search-toggle]');
    if (!toggle) return;
    var header = toggle.closest('.site-header') || document;
    var panel = header.querySelector('.search-panel');
    if (panel) toggleSearch(panel, !panel.hasAttribute('data-open'));
  });

  var searchTimer = null;
  document.addEventListener('input', function (event) {
    var input = event.target.closest('[data-predictive-input]');
    if (!input) return;
    var scope = input.closest('.search-panel') || document;
    var results = scope.querySelector('[data-predictive-results]');
    if (!results) return;
    var status = scope.querySelector('[data-predictive-status]');

    function announceCount(count) {
      if (!status) return;
      var template = status.getAttribute('data-results-template') || '{{ count }}';
      status.textContent = template.replace(/\{\{\s*count\s*\}\}/, count);
    }

    var term = input.value.trim();
    clearTimeout(searchTimer);
    if (term.length < 2) {
      results.innerHTML = '';
      if (status) status.textContent = '';
      return;
    }
    searchTimer = setTimeout(function () {
      var url =
        window.Shopify && window.Shopify.routes && window.Shopify.routes.root
          ? window.Shopify.routes.root
          : '/';
      fetch(
        url +
          'search/suggest.json?q=' +
          encodeURIComponent(term) +
          '&resources[type]=product&resources[limit]=5'
      )
        .then(function (response) {
          return response.json();
        })
        .then(function (data) {
          var products =
            (data.resources &&
              data.resources.results &&
              data.resources.results.products) ||
            [];
          if (!products.length) {
            results.innerHTML = '';
            announceCount(0);
            return;
          }
          var list = document.createElement('ul');
          list.className = 'search-suggestions';
          products.forEach(function (product) {
            var item = document.createElement('li');
            var link = document.createElement('a');
            link.href = product.url;
            if (product.featured_image && product.featured_image.url) {
              var img = document.createElement('img');
              img.src = product.featured_image.url + '&width=96';
              img.alt = '';
              img.width = 48;
              img.height = 60;
              img.loading = 'lazy';
              link.appendChild(img);
            }
            var title = document.createElement('span');
            title.className = 'search-suggestions__title';
            title.textContent = product.title;
            link.appendChild(title);
            item.appendChild(link);
            list.appendChild(item);
          });
          results.innerHTML = '';
          results.appendChild(list);
          announceCount(products.length);
        })
        .catch(function () {
          results.innerHTML = '';
        });
    }, 250);
  });

  /* ---------- Auto-submit selects (toolbar sort) ---------- */
  document.addEventListener('change', function (event) {
    var el = event.target.closest('[data-auto-submit]');
    if (el && el.form) el.form.submit();
  });

  /* ---------- Post-submit form status ---------- */
  // Server-rendered success/error messages exist at page load, so live-region
  // roles alone never announce them. Move focus to the first one instead.
  var status = document.querySelector('[data-autofocus-status]');
  if (status) status.focus();

  /* ---------- Quantity steppers ---------- */
  document.addEventListener('click', function (event) {
    var button = event.target.closest('[data-qty-change]');
    if (!button) return;
    var wrapper = button.closest('.qty-input');
    var input = wrapper && wrapper.querySelector('input[type="number"]');
    if (!input) return;
    var step = button.getAttribute('data-qty-change') === 'up' ? 1 : -1;
    var min = parseInt(input.min || '1', 10);
    var next = (parseInt(input.value, 10) || min) + step;
    if (next < min) next = min;
    if (input.max && next > parseInt(input.max, 10)) next = parseInt(input.max, 10);
    input.value = next;
    input.dispatchEvent(new Event('change', { bubbles: true }));
    announceQuantity(input);
  });

  /* ---------- Cart stock cap ---------- */
  // A cart can hold more than is in stock (quantities typed or added before
  // the cap existed). On the cart page, pull any such line down to the
  // available amount and save — the server-rendered notice explains why.
  (function () {
    var over = Array.prototype.filter.call(
      document.querySelectorAll('input[name="updates[]"][data-max]'),
      function (i) { return parseInt(i.getAttribute('data-current'), 10) > parseInt(i.getAttribute('data-max'), 10); }
    );
    if (!over.length) return;
    over.forEach(function (i) { i.value = i.getAttribute('data-max'); });
    var form = over[0].form;
    var flag = document.createElement('input');
    flag.type = 'hidden';
    flag.name = 'update';
    flag.value = '1';
    form.appendChild(flag);
    form.submit();
  })();

  /* ---------- Cart quantity auto-update ---------- */
  // On the cart page a quantity change (stepper or typed) saves right away:
  // submit the cart form as an "update", same as the no-JS Update button.
  // Debounced so a quick run of + clicks becomes one request.
  var cartUpdateTimer = null;
  document.addEventListener('change', function (event) {
    var input = event.target;
    if (!input.matches || !input.matches('input[name="updates[]"]')) return;
    var form = input.form;
    if (!form) return;
    clearTimeout(cartUpdateTimer);
    cartUpdateTimer = setTimeout(function () {
      var flag = document.createElement('input');
      flag.type = 'hidden';
      flag.name = 'update';
      flag.value = '1';
      form.appendChild(flag);
      form.submit();
    }, 600);
  });

  // One shared live region so stepper buttons (which keep focus) still get
  // the new quantity announced to screen readers.
  var qtyStatus = null;
  function announceQuantity(input) {
    if (!qtyStatus) {
      qtyStatus = document.createElement('p');
      qtyStatus.className = 'visually-hidden';
      qtyStatus.setAttribute('role', 'status');
      document.body.appendChild(qtyStatus);
    }
    var label = input.labels && input.labels[0] ? input.labels[0].textContent.trim() : '';
    qtyStatus.textContent = (label ? label + ' ' : '') + input.value;
  }
})();
